/**
 * DashboardGrid - the dashboard, rendered from a layout instead of by hand.
 *
 * What used to be seven hand-written rows in `HomePage` is now the product of
 * three things that know nothing about each other: `widgetLayout` (a pure
 * tiled-grid reducer), `widgets/registry` (what each widget is and who may see
 * it), and this file (the twelve-column CSS that draws the result).
 *
 * The DEFAULT layout is no longer one grid for everyone: it is built per user
 * by `dashboardPersona` from the organisation's type and whether this person
 * administers it, so a first visit lands on an arrangement that already suits
 * the work rather than on a generic wall. A STORED layout always wins over it —
 * the persona decides only what someone sees before they have arranged
 * anything, and what "Reset to default" gives back.
 *
 * TWO MODES, one renderer. Out of edit mode this is exactly what it was: the
 * layout is loaded, gated and drawn, and nothing is draggable or resizable.
 * `editing` adds the chrome - a drag handle bar per widget, a resize edge, a
 * height stepper, a remove ×, and the drop zones between cells - without
 * changing how a single widget is placed. Every change goes straight through
 * `useWidgetLayout` to the user-scoped store, so leaving edit mode saves
 * nothing: it only stops drawing the chrome.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  PiArrowsHorizontalLight,
  PiArrowsVerticalLight,
  PiCheckLight,
  PiDotsSixVerticalLight,
  PiArrowCounterClockwiseLight,
  PiPlusLight,
  PiSquaresFourLight,
  PiXLight,
} from 'react-icons/pi';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import { showModal } from '../../store/actions/modalActions';
import { DashboardState } from '../../store/reducer/DashboardReducer';
import GlassCard from '../shared/brand/GlassCard';
import Button from '../shared/TailwindComponents/Button';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import usePanelDrag from '../shared/surface/usePanelDrag';
import {
  SPAN_UNITS,
  PlacedWidget,
  WidgetGrid,
  WidgetHeight,
  WidgetSlot,
  flowRows,
  isSingleColumn,
} from './widgetLayout';
import buildDashboardGrid, { personaKey, resolvePersona } from './dashboardPersona';
import useWidgetLayout from './useWidgetLayout';
import WidgetSpanHandle from './WidgetSpanHandle';
import {
  DashboardWidgetId,
  WIDGETS,
  WIDGET_IDS,
  widgetAllowed,
} from './widgets/registry';

/* ------------------------------------------------------------------ */
/*  The default arrangement                                            */
/* ------------------------------------------------------------------ */

/**
 * The grid a component falls back to, and the one the tests describe: the
 * arrangement a member of a law firm sees on their first visit.
 *
 * The REAL grid is built per user - see `dashboardPersona`, and `useDashboardGrid`
 * below. This constant is the `legal`, non-admin persona, which is what the
 * dashboard has always shipped, so anything that imports it (a test, a tour)
 * keeps describing the same page it used to.
 */
export const DASHBOARD_GRID: WidgetGrid<DashboardWidgetId> = buildDashboardGrid({
  audience: 'legal',
  isOrgAdmin: false,
});

/**
 * This user's grid.
 *
 * Memoised on the PERSONA KEY rather than on the persona object: the object is
 * rebuilt every render, and `useWidgetLayout` reloads from storage whenever
 * the grid identity changes - an unmemoised grid would re-read (and re-render)
 * on every paint. The key only changes when the org type or the admin flag
 * actually does, which is at most once per session, and exactly when a reload
 * IS wanted: that is the moment we learn who this is.
 */
const useDashboardGrid = (data: DashboardState): WidgetGrid<DashboardWidgetId> => {
  const orgType = useAppSelector((state: RootState) => state.user.userInfo?.org_type);
  const persona = resolvePersona({
    orgType,
    isOrgAdmin: data.overview?.is_org_admin,
  });
  const key = personaKey(persona);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => buildDashboardGrid(persona), [key]);
};

/* ------------------------------------------------------------------ */
/*  Rendering                                                          */
/* ------------------------------------------------------------------ */

/**
 * Span → column class. Written out in full because Tailwind reads the source
 * for class names; a template string would produce nothing.
 */
const COLUMN_CLASS: Record<number, string> = {
  // A quarter is ~220px at lg next to the sidebar - KPI tiles truncated to
  // "2. Op Ta". Quarters are halves until xl; a row of them wraps two by two.
  3: 'lg:col-span-6 xl:col-span-3',
  6: 'lg:col-span-6',
  9: 'lg:col-span-6 xl:col-span-9',
  12: 'lg:col-span-12',
};

/**
 * Height → a FLOOR and a CEILING for the cell, from `lg` up.
 *
 * THE TWO WRONG MODELS THIS SETTLES BETWEEN, because it has been both:
 *
 *   • `min-h` ALONE (the original) did nothing. A floor only bites when the
 *     content is shorter than it, and every real widget was taller, so S, M
 *     and L were one height wearing three labels.
 *   • A DEFINITE `h` (the first fix) made the stepper work and made the page
 *     worse: every card was exactly its preset, so a column with three rows
 *     got a cramped box with a tiny scrollbar, and a card with two
 *     notifications got a one-row peep-hole. A height is not a size you want
 *     to commit content to.
 *
 * A floor AND a ceiling gives all three regimes the right behaviour:
 *
 *     content < floor    → the box is the floor. Generous, no scrollbar.
 *     floor..ceiling     → the box is the CONTENT. Nothing clipped, nothing
 *                          scrolled, no dead space. This is where a widget
 *                          spends most of its life.
 *     content > ceiling  → the box is the ceiling and the widget scrolls
 *                          inside itself, which is the only time a scrollbar
 *                          is the honest answer.
 *
 * THE SCALE IS VIEWPORT-AWARE AT THE TOP. `lg` is "as tall as the screen
 * allows" - `calc(100vh - 14rem)` leaves room for the app header, the page
 * padding and the row above - floored at 36rem so a short laptop still gets a
 * substantial card rather than a sliver. The work card ships on `lg`, which is
 * how it fills the screen instead of ending after three rows.
 *
 * (If a ceiling ever computes below its floor - a very short window on `lg` —
 * CSS resolves min-height over max-height, so the card stays usable.)
 */
const HEIGHT_CLASS: Record<WidgetHeight, string> = {
  /* xs max-h was 12rem, calibrated against the old scrollport padding. The
     larger shadow padding makes a row with a taller sibling (e.g. greeting next
     to "At a glance") settle around 13.5rem, and a 12rem cap stopped the short
     xs card from stretching to meet it — so the row looked ragged. Raise the
     cap to 15rem (matching the edit-mode xs) so items-stretch can equalise;
     a compact xs card still only grows when a sibling forces the row taller. */
  xs: 'lg:min-h-[6rem] lg:max-h-[15rem]',
  sm: 'lg:min-h-[11rem] lg:max-h-[24rem]',
  md: 'lg:min-h-[28rem] lg:max-h-[44rem]',
  lg: 'lg:min-h-[max(36rem,calc(100vh_-_14rem))] lg:max-h-[calc(100vh_-_6rem)]',
};

/**
 * The same scale plus the edit-mode chrome bar (≈2.5rem) and its gap.
 *
 * Without this the widget visibly shrinks the moment you press Customize —
 * you would be sizing a card while looking at a smaller one, and the S you
 * picked would not be the S you got back.
 */
const EDIT_HEIGHT_CLASS: Record<WidgetHeight, string> = {
  xs: 'lg:min-h-[9rem] lg:max-h-[15rem]',
  sm: 'lg:min-h-[14rem] lg:max-h-[27rem]',
  md: 'lg:min-h-[31rem] lg:max-h-[47rem]',
  lg: 'lg:min-h-[max(39rem,calc(100vh_-_11rem))] lg:max-h-[calc(100vh_-_3rem)]',
};

/* ------------------------------------------------------------------ */
/*  Edit chrome                                                        */
/* ------------------------------------------------------------------ */

interface WidgetChromeProps {
  slot: WidgetSlot<DashboardWidgetId>;
  dragging: boolean;
  /** Where this widget sits among the visible ones - "3 of 9". */
  position: { index: number; total: number };
  onDragStart: (e: React.PointerEvent) => void;
  onMoveBy: (delta: number) => void;
  onRemove: () => void;
}

/**
 * The bar above a widget in edit mode: its name, and everything you can do to
 * it that is not a drag of the right edge.
 *
 * It is a `GlassCard` header - the same compact bar a floating pane uses as
 * its handle - rather than a bar drawn by hand, and it sits ABOVE the widget
 * instead of wrapping it, so the section component's own card is not nested
 * inside a second sheet of glass.
 */
const WidgetChrome: React.FC<WidgetChromeProps> = ({
  slot,
  dragging,
  position,
  onDragStart,
  onMoveBy,
  onRemove,
}) => {
  const { t } = useTranslation();
  const definition = WIDGETS[slot.id];

  /*
    THE BAR IS ALSO A KEYBOARD CONTROL. A drag handle that only answers to a
    pointer makes rearranging the dashboard impossible without a mouse, and
    "impossible without a mouse" is not a customisation feature. Left/right
    step the widget through the arrangement; the same keys the span handle
    uses, one level up.
  */
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      onMoveBy(-1);
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      onMoveBy(1);
    }
  };

  return (
    <GlassCard
      density="compact"
      padding="none"
      icon={<PiDotsSixVerticalLight />}
      title={t(definition.label)}
      className={clsx('flex-shrink-0', dragging && 'opacity-60')}
      headerProps={{
        onPointerDown: onDragStart,
        onKeyDown,
        tabIndex: 0,
        role: 'button',
        'aria-label': t('{{label}} — position {{index}} of {{total}}. Use the arrow keys to move it.', {
          label: t(definition.label),
          index: position.index,
          total: position.total,
        }),
        // The whole bar is the handle: making only the title text draggable
        // leaves most of a widget's chrome dead to the pointer.
        className: clsx(
          'cursor-grab select-none focus:outline-none focus:ring-1 focus:ring-champagne/60',
          dragging && 'cursor-grabbing'
        ),
        'data-testid': `widget-handle-${slot.id}`,
      } as React.HTMLAttributes<HTMLDivElement>}
      actions={(
        <>
          {/*
            No height stepper (UI feedback D-1): resizing a widget taller only
            padded it with dead space - each widget already carries the right
            height for its content from the registry. Reordering, width and
            remove are the edits that mean something.
          */}
          <button
            type="button"
            data-testid={`widget-remove-${slot.id}`}
            aria-label={t('Remove widget')}
            title={t('Remove widget')}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onRemove}
            className="rounded-md p-0.5 text-gray-400 transition-colors duration-200 hover:text-danger-600 dark:text-gray-500 dark:hover:text-danger-400"
          >
            <PiXLight className="h-4 w-4" />
          </button>
        </>
      )}
    >
      <span aria-hidden />
    </GlassCard>
  );
};

interface DropZone {
  /** Where in the model a drop here lands - see `moveWidget`. */
  row: number;
  col: number;
  /** Visual row, and how far along it the zone sits (0–1). */
  rowIndex: number;
  fraction: number;
}

interface GridRowProps {
  row: PlacedWidget<DashboardWidgetId>[];
  data: DashboardState;
  editing: boolean;
  /** Drag/resize are off below `lg`, where every widget is full width anyway. */
  interactive: boolean;
  dragging: DashboardWidgetId | null;
  zones: { index: number; fraction: number }[];
  isHovered: (index: number) => boolean;
  onDragStart: (id: DashboardWidgetId, e: React.PointerEvent) => void;
  onSpan: (id: DashboardWidgetId, span: number) => void;
  onMoveBy: (id: DashboardWidgetId, delta: number) => void;
  onRemove: (id: DashboardWidgetId) => void;
  /** The page's one refresh, handed down to the widgets that offer one. */
  onRefresh: () => void;
  /** Where each widget sits among the visible ones, for the chrome's label. */
  positionOf: (id: DashboardWidgetId) => { index: number; total: number };
}

/**
 * One packed row.
 *
 * A component of its own only because the resize handle has to measure the
 * row it lives in - a px delta means nothing until it is divided by the row's
 * width - and a ref per row cannot be taken inside a `map`.
 */
const GridRow: React.FC<GridRowProps> = ({
  row,
  data,
  editing,
  interactive,
  dragging,
  zones,
  isHovered,
  onDragStart,
  onSpan,
  onMoveBy,
  onRemove,
  onRefresh,
  positionOf,
}) => {
  const rowRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative">
      <div
        ref={rowRef}
        /*
          One column below `lg` - the same rule `flowRows({singleColumn})`
          states for consumers that measure, expressed here in CSS so the
          page needs no resize listener to honour it.

          `lg:items-stretch` (the grid default) gives every cell in a row the
          height of the tallest one, so cards in a row line up instead of sitting
          at ragged content-heights. This used to be `items-start` to let the
          per-widget height stepper set individual heights, but that stepper was
          removed (D-1), so start-alignment only left the row uneven. Each card
          is `h-full`, so it fills the equalised cell.
        */
        className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 lg:items-stretch"
      >
        {row.map(({ slot, span, columns }) => {
          const definition = WIDGETS[slot.id];
          const minSpan = WIDGETS[slot.id].minSpan ?? 1;
          return (
            <div
              key={slot.id}
              /*
                THE WHOLE CARD IS THE DRAG HANDLE.

                It used to be only the thin chrome bar, and the card below it
                was `pointer-events-none` - so the obvious target, the widget
                itself, was inert, and "dragging does nothing" was a fair
                description of pressing anywhere on it. Because that body is
                not a hit target, a press on it lands on THIS element, which
                now starts the gesture. The chrome bar's own handler still runs
                first and calls `preventDefault`, so a press there is not
                counted twice; the resize edge stops propagation, so a press
                on it is a resize and never a move.
              */
              onPointerDown={
                editing && interactive
                  ? (event) => {
                    if (event.defaultPrevented) return;
                    onDragStart(slot.id, event);
                  }
                  : undefined
              }
              className={clsx(
                // The widget is what its contents measure themselves against:
                // spans change without the viewport moving. See index.css.
                'relative widget-canvas flex flex-col',
                COLUMN_CLASS[columns] || COLUMN_CLASS[12],
                (editing ? EDIT_HEIGHT_CLASS : HEIGHT_CLASS)[slot.height]
                  || HEIGHT_CLASS.md,
                editing && 'gap-2',
                // The affordance the mode was missing: in edit mode the card
                // says it can be picked up before you try.
                editing && interactive && 'cursor-grab rounded-lg ring-1 ring-transparent transition duration-200 ease-slide hover:ring-champagne/40',
                dragging === slot.id && 'cursor-grabbing opacity-40 ring-champagne/70'
              )}
              data-tour-id={definition.tourId}
              data-widget-id={slot.id}
              data-widget-height={slot.height}
              data-widget-draggable={editing && interactive ? 'true' : undefined}
            >
              {editing && (
                <WidgetChrome
                  slot={slot}
                  dragging={dragging === slot.id}
                  position={positionOf(slot.id)}
                  onDragStart={(e) => onDragStart(slot.id, e)}
                  onMoveBy={(delta) => onMoveBy(slot.id, delta)}
                  onRemove={() => onRemove(slot.id)}
                />
              )}
              {/*
                In edit mode the widget is a picture of itself: its own links
                and buttons must not fire, or arranging the dashboard would
                navigate off it.
              */}
              {/*
                The widget fills the height on offer, and anything past it
                scrolls - but WHICH thing scrolls is the widget's own business.

                `[&>*]:flex-1` (and not the `min-h-full` percentage this used
                to be) makes the section's root a flex item that GROWS into the
                box. Percentages need a definite parent height, and with a
                floor/ceiling cell the parent is often `auto` - flex has no
                such requirement, so this fills in all three regimes above.

                Note there is deliberately no `min-h-0` on that child. A flex
                item's automatic minimum is its content, so:
                  • a card that manages its own scrolling (`GlassCard fill`
                    plus an inner `min-h-0 flex-1 overflow-y-auto`, which is
                    every list widget) reports a small minimum, settles at the
                    box height, and scrolls INSIDE itself;
                  • one that does not refuses to shrink below its content and
                    lets this wrapper scroll instead.
                Adding `min-h-0` here would let the second kind be squeezed and
                clipped by its own `overflow-hidden`.

                In edit mode the widget is a picture of itself: its own links
                and buttons must not fire, or arranging the dashboard would
                navigate off it.
              */}
              {/*
                ROOM FOR THE SHADOW.

                `overflow-y-auto` makes this a scrollport, and a scrollport
                clips at its PADDING box - on all four sides, not just the one
                it scrolls. With no padding the clip fell exactly on the card's
                own edge, so every glass shadow was sliced off flat and the
                dashboard read as a grid of hard boxes rather than cards
                floating on the page.

                The padding is sized to the shadow rather than picked by eye:
                `.glass` draws `0 16px 40px -16px` plus `0 4px 12px -4px`,
                which reaches about 20px below the card, ~4px to each side and
                nothing above it. So the box gets that much room to spill into
                and the shadow is drawn whole. The scrolling this wrapper does
                for widgets that do not scroll themselves is unaffected.
              */}
              <div
                className={clsx(
                  'custom-scrollbar-main flex min-h-0 flex-1 flex-col overflow-y-auto [&>*]:flex-1',
                  /* Room for the (now smooth, layered) shadow + the 3px hover
                     lift so the scrollport never slices the cast into a hard
                     rectangle. The shadow is tucked with negative spread, so it
                     pools below (~21px rest, ~29px hover) and barely reaches the
                     sides — this padding fits it whole. */
                  'px-2 pt-2 pb-8',
                  /* `dashboard-card` styles the CHILD - the widget's own
                     container - so every card on the page takes one shared
                     elevation. See `.dashboard-card` in index.css. A widget
                     that `floats` (several cards, not one) is skipped: its
                     root is a row of cards, and elevating it drew a card
                     around the cards. It elevates its own cards instead. */
                  !definition.floats && 'dashboard-card',
                  editing && 'pointer-events-none select-none'
                )}
              >
                {definition.render({ data, loading: data.loading, onRefresh })}
              </div>
              {editing && interactive && (
                <WidgetSpanHandle
                  rowRef={rowRef}
                  span={span}
                  minSpan={minSpan}
                  label={`${WIDGETS[slot.id].label} width`}
                  onResize={(next) => onSpan(slot.id, next)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/*
        The drop net. Zones are `pointer-events-none` and hit-tested from the
        window by `usePanelDrag` - the same reason the panes gave up on HTML5
        drag-and-drop, which never completed a drop between positioned docks.
      */}
      {dragging && interactive && (
        <div className="pointer-events-none absolute -inset-y-2 inset-x-0 z-20">
          {zones.map((zone) => (
            <div
              key={zone.index}
              data-dropzone="true"
              data-dock="left"
              data-index={zone.index}
              data-testid={`widget-dropzone-${zone.index}`}
              /*
                WIDE ENOUGH TO HIT. These were 24px slivers, which is a target
                you have to aim at while already holding something - half of
                "the drag does nothing" was releasing two pixels off one. The
                catch area is 64px; what is DRAWN inside it is still a hairline,
                so the page does not turn into a row of bars.
              */
              className="absolute top-0 h-full w-16 -ms-8 flex items-stretch justify-center"
              style={{ left: `${zone.fraction * 100}%` }}
            >
              <span
                aria-hidden
                className={clsx(
                  'my-1 rounded-full transition-all duration-200 ease-slide',
                  isHovered(zone.index)
                    ? 'w-1.5 bg-champagne ring-4 ring-champagne/20'
                    : 'w-0.5 bg-champagne/40'
                )}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  The grid                                                           */
/* ------------------------------------------------------------------ */

interface DashboardGridProps {
  data: DashboardState;
  /** Chrome on, widgets inert. The layout is saved either way. */
  editing?: boolean;
  onExitEditing?: () => void;
  /** Re-run the page's request. Handed to widgets that offer a refresh. */
  onRefresh?: () => void;
  /**
   * Hand the page a way to reset the layout.
   *
   * The grid owns `useWidgetLayout`, so the reset lives here - but "put my
   * dashboard back" is exactly the thing someone wants when they have made a
   * mess and do NOT know that the way out is behind a Customize button. The
   * page takes this and puts it in the header, where it is reachable without
   * entering edit mode at all.
   */
  registerReset?: (reset: () => void) => void;
}

const viewportWidth = (): number =>
  typeof window === 'undefined' ? 1440 : window.innerWidth;

const DashboardGrid: React.FC<DashboardGridProps> = ({
  data,
  editing = false,
  onExitEditing,
  onRefresh,
  registerReset,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { hasSectionAccess, hasAiFeatureAccess } = useLicenseGate();
  /*
    The ops are pulled out one by one rather than used off `grid`: each is a
    stable callback, while the object around them is new every render, and the
    drop handler below is a dependency of the window-level drag listeners.
  */
  const dashboardGrid = useDashboardGrid(data);
  const grid = useWidgetLayout(dashboardGrid);
  const { layout, move, moveBy, setVisible, setSpan, reset } = grid;

  const [width, setWidth] = useState<number>(viewportWidth);
  useEffect(() => {
    const onResize = () => setWidth(viewportWidth());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Below `lg` every widget is full width and stacks, so there is nothing for
  // a reorder drag or a width handle to mean.
  const interactive = !isSingleColumn(width);

  const allow = useCallback(
    (id: DashboardWidgetId) =>
      widgetAllowed(id, { data, hasSectionAccess, hasAiFeatureAccess }),
    [data, hasSectionAccess, hasAiFeatureAccess]
  );

  const rows = useMemo(
    () => flowRows(dashboardGrid, layout, { allow }),
    [dashboardGrid, layout, allow]
  );

  /**
   * Where each visible widget sits in the arrangement, for the chrome's
   * accessible name. Derived from the RENDERED rows, not the stored order, so
   * "3 of 9" counts what is actually on the page - a licence-gated widget is
   * not a position the user can move something to.
   */
  const positions = useMemo(() => {
    const flat = rows.flat().map((placed) => placed.slot.id);
    const map = {} as Record<DashboardWidgetId, { index: number; total: number }>;
    flat.forEach((id, index) => {
      map[id] = { index: index + 1, total: flat.length };
    });
    return map;
  }, [rows]);

  const positionOf = useCallback(
    (id: DashboardWidgetId) => positions[id] ?? { index: 1, total: 1 },
    [positions]
  );

  /**
   * Every place a dragged widget may land, in visual order.
   *
   * A zone's model position is taken from the widget it sits IN FRONT OF, not
   * from the visual row it is drawn on: a licence-gated widget is missing from
   * the flow but still holds a place in the order, so counting rendered rows
   * would drop widgets in the wrong slot on exactly those dashboards.
   */
  const zones = useMemo<DropZone[]>(() => {
    const list: DropZone[] = [];
    rows.forEach((row, rowIndex) => {
      let used = 0;
      row.forEach((placed) => {
        list.push({
          row: placed.slot.row,
          col: placed.slot.col,
          rowIndex,
          fraction: used / SPAN_UNITS,
        });
        used += placed.span;
      });
      const last = row[row.length - 1];
      if (last) {
        // Past the end of the row: everything on it stays in front.
        list.push({
          row: last.slot.row,
          col: last.slot.col + last.slot.span,
          rowIndex,
          fraction: 1,
        });
      }
    });
    return list;
  }, [rows]);

  const drag = usePanelDrag<DashboardWidgetId>(
    useCallback(
      (id, target) => {
        const zone = zones[target.index];
        if (zone) move(id, zone.row, zone.col);
      },
      [zones, move]
    ),
    editing && interactive
  );

  /*
    WHERE THE POINTER IS, so the drag can be SEEN.

    Before this the only feedback was the source card going half-transparent
    and a champagne sliver lighting up somewhere else on the row - nothing was
    attached to the cursor, so a drag in progress looked like a click that had
    broken the page. A small chip follows the pointer carrying the widget's
    name; it is the cheapest possible "you are holding this".
  */
  const [ghost, setGhost] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (!drag.dragging) {
      setGhost(null);
      return undefined;
    }
    const move = (event: PointerEvent) => setGhost({ x: event.clientX, y: event.clientY });
    window.addEventListener('pointermove', move);
    return () => window.removeEventListener('pointermove', move);
  }, [drag.dragging]);

  const { onDragStart, onDragMove, onDragEnd } = drag;
  const startDrag = useCallback(
    (id: DashboardWidgetId, e: React.PointerEvent) => {
      if (!editing || !interactive) return;
      if (typeof e.button === 'number' && e.button > 0) return;
      e.preventDefault();
      onDragStart(id);
      onDragMove(e.clientX, e.clientY);
    },
    [editing, interactive, onDragStart, onDragMove]
  );

  /*
    Leaving edit mode mid-drag would leave the window listeners holding a
    widget nobody can drop - the exact freeze `usePanelDrag`'s net exists to
    prevent, arrived at from the other side.
  */
  useEffect(() => {
    if (!editing && drag.dragging) onDragEnd();
  }, [editing, drag.dragging, onDragEnd]);

  /** Registered, hidden, and licensed - what the gallery may offer. */
  const available = useMemo(
    () => WIDGET_IDS.filter((id) => !layout.slots[id]?.visible && allow(id)),
    [layout, allow]
  );

  /**
   * Reset asks first.
   *
   * Every other edit is reversible by doing the opposite - re-add the widget,
   * drag it back, widen it again. Reset is the only one that throws away an
   * arrangement someone built, and it is a single click next to "Done".
   */
  const confirmReset = useCallback(() => {
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        title: t('Reset your dashboard'),
        message: t('This puts every widget back where it started.'),
        subText: t('Widgets you added or removed, and any resizing, will be lost.'),
        icon: <PiArrowCounterClockwiseLight className="h-6 w-6 text-warning-600" />,
        variant: 'warning',
        confirmLabel: t('Reset'),
        onConfirm: () => reset(),
      })
    );
  }, [dispatch, t, reset]);

  useEffect(() => {
    registerReset?.(confirmReset);
  }, [registerReset, confirmReset]);

  /**
   * What is ALREADY on the dashboard.
   *
   * The gallery lists the whole catalogue now, not just the leftovers: a panel
   * offering two cards reads as "this is all a dashboard can be", when in fact
   * it means "you already have the other nine". Placed widgets are shown, and
   * shown as placed.
   */
  const placed = useMemo(
    () => WIDGET_IDS.filter((id) => layout.slots[id]?.visible && allow(id)),
    [layout, allow]
  );

  const openGallery = useCallback(() => {
    dispatch(
      showSidePanel({
        type: 'dashboardWidgets',
        showSidePanel: true,
        width: '32rem',
        message: t('Widgets'),
        subMsg: t('Everything your dashboard can show. Add what you want, in any order.'),
        available,
        placed,
        onAdd: (id: DashboardWidgetId) => setVisible(id, true),
        onRemove: (id: DashboardWidgetId) => setVisible(id, false),
        onCancel: () => dispatch(hideSidePanel()),
      })
    );
  }, [dispatch, t, available, placed, setVisible]);

  return (
    /*
      FULL WIDTH. This was `mx-auto max-w-7xl`, which capped the page at
      1280px: on a 27" monitor a third of the window was margin, and a widget
      the user had deliberately widened still could not use the space they
      widened it into. The grid is the viewport now, and the responsiveness
      lives where it belongs - the twelve-column row (one column below `lg`,
      matching `isSingleColumn`) and the container queries inside each widget,
      which measure the CARD rather than the window, so a half-width card lays
      itself out the same whatever screen it is half of.
    */
    /* `p-5` and not `p-4`: the outermost cards need the same room to cast a
       shadow that the ones between them get from the gutter. */
    <div className="w-full space-y-4 p-5 lg:p-6">
      {editing && (
        /*
          THE TOOLBAR IS THE INSTRUCTIONS.

          Customize was reported as hard to grasp, and the honest reason is
          that it never said what it was: one line of prose, three buttons, and
          a page that looked almost exactly as it had a second earlier. So the
          bar states the three things you can do, in the order you would do
          them, each next to the mark you would use to do it - a grab handle,
          a resize bar, an S/M/L. What is left is the difference between a mode
          you have to be told about and one you can read.
        */
        <GlassCard padding="sm" data-testid="dashboard-edit-toolbar">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-sm font-medium text-fg-strong">
                <span aria-hidden className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-champagne" />
                {t('Customizing your dashboard')}
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                  {t('Every change saves as you make it')}
                </span>
              </p>
              {interactive ? (
                <ul className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                  <li className="flex items-center gap-1.5">
                    <PiDotsSixVerticalLight aria-hidden className="h-3.5 w-3.5 text-champagne" />
                    {t('Drag any card to move it')}
                  </li>
                  <li className="flex items-center gap-1.5">
                    <PiArrowsHorizontalLight aria-hidden className="h-3.5 w-3.5 text-champagne" />
                    {t('Drag its right edge to resize')}
                  </li>
                  <li className="flex items-center gap-1.5">
                    <PiArrowsVerticalLight aria-hidden className="h-3.5 w-3.5 text-champagne" />
                    {t('S · M · L sets its height')}
                  </li>
                  <li className="flex items-center gap-1.5">
                    <PiXLight aria-hidden className="h-3.5 w-3.5 text-champagne" />
                    {t('× removes it — Widgets puts it back')}
                  </li>
                </ul>
              ) : (
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {t('Widgets stack on a narrow screen — widen the window to rearrange.')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="small"
                onClick={openGallery}
                icon={<PiPlusLight className="h-4 w-4" />}
                data-testid="dashboard-add-widget"
              >
                {available.length > 0
                  ? t('Widgets ({{count}} to add)', { count: available.length })
                  : t('Widgets')}
              </Button>
              {/*
                RESET LIVES HERE AND ONLY HERE now. It used to ALSO be a
                permanent header action next to Refresh - a destructive button
                one click away from someone who was not customising anything.
              */}
              <Button
                variant="neutral"
                size="small"
                onClick={confirmReset}
                icon={<PiArrowCounterClockwiseLight className="h-4 w-4" />}
                data-testid="dashboard-reset-layout"
              >
                {t('Reset to default')}
              </Button>
              {onExitEditing && (
                <Button
                  variant="primary"
                  size="small"
                  onClick={onExitEditing}
                  icon={<PiCheckLight className="h-4 w-4" />}
                  data-testid="dashboard-done-editing"
                >
                  {t('Done')}
                </Button>
              )}
            </div>
          </div>
        </GlassCard>
      )}

      {/* What you are holding, next to the pointer holding it. */}
      {ghost && drag.dragging && (
        <div
          aria-hidden
          data-testid="dashboard-drag-ghost"
          className="glass pointer-events-none fixed z-[400] flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-fg-strong shadow-sm"
          style={{ left: ghost.x + 14, top: ghost.y + 14 }}
        >
          <PiDotsSixVerticalLight aria-hidden className="h-3.5 w-3.5 text-champagne" />
          {t(WIDGETS[drag.dragging].label)}
        </div>
      )}

      {rows.length === 0 ? (
        /*
          Every widget hidden, or every one gated away. Either way the page is
          blank, and a blank page with no explanation reads as a failed load —
          which is exactly what a dashboard you emptied yourself must not do.
        */
        <GlassCard padding="none" className="overflow-hidden">
          <EmptyState
            icon={<PiSquaresFourLight />}
            headline={t('Your dashboard is empty')}
            subtext={t('Add the cards you want to see. You can always start over from the default.')}
            button={
              editing
                ? { label: t('Add widget'), onClick: openGallery }
                : { label: t('Reset to default'), onClick: confirmReset }
            }
          />
        </GlassCard>
      ) : rows.map((row, rowIndex) => (
        <GridRow
          key={row.map((placed) => placed.slot.id).join('+')}
          row={row}
          data={data}
          editing={editing}
          interactive={interactive}
          dragging={drag.dragging}
          zones={zones
            .map((zone, index) => ({ ...zone, index }))
            .filter((zone) => zone.rowIndex === rowIndex)}
          isHovered={(index) => drag.isHovered('left', index)}
          onDragStart={startDrag}
          onSpan={setSpan}
          onMoveBy={(id, delta) => moveBy(id, delta, allow)}
          onRemove={(id) => setVisible(id, false)}
          onRefresh={() => onRefresh?.()}
          positionOf={positionOf}
        />
      ))}
    </div>
  );
};

export default DashboardGrid;
